import imageCompression from "browser-image-compression";

const BYTES_PER_MB = 1024 * 1024;

export const DEFAULT_PROTECTED_IMAGE_OPTIONS = {
  allowedTypes: ["image/jpeg", "image/png", "image/webp"],
  maxInputSizeMB: 10,
  maxOutputSizeMB: 1,
  maxWidthOrHeight: 512,
  useWebWorker: true,
} as const;

export type ProtectedImageOptimizationOptions = Partial<{
  allowedTypes: readonly string[];
  maxInputSizeMB: number;
  maxOutputSizeMB: number;
  maxWidthOrHeight: number;
  useWebWorker: boolean;
}>;

const formatTypeList = (types: readonly string[]): string =>
  types.map((type) => type.replace("image/", "").toUpperCase()).join(", ");

export const validateProtectedImageFile = (
  file: File,
  options: ProtectedImageOptimizationOptions = {},
): void => {
  const merged = { ...DEFAULT_PROTECTED_IMAGE_OPTIONS, ...options };

  if (!merged.allowedTypes.includes(file.type)) {
    throw new Error(
      `Please choose a supported image type: ${formatTypeList(merged.allowedTypes)}.`,
    );
  }

  const maxInputBytes = merged.maxInputSizeMB * BYTES_PER_MB;
  if (file.size > maxInputBytes) {
    throw new Error(
      `Please choose an image smaller than ${merged.maxInputSizeMB}MB.`,
    );
  }
};

export const optimizeProtectedImage = async (
  file: File,
  options: ProtectedImageOptimizationOptions = {},
): Promise<File> => {
  const merged = { ...DEFAULT_PROTECTED_IMAGE_OPTIONS, ...options };
  validateProtectedImageFile(file, merged);

  return imageCompression(file, {
    maxSizeMB: merged.maxOutputSizeMB,
    maxWidthOrHeight: merged.maxWidthOrHeight,
    useWebWorker: merged.useWebWorker,
  });
};
